import { API_BASE_URL } from "../../config";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearPermissionRecovery } from "../Utils/permissionNotice";

const Login = ({ setLoggedIn, setUser, setToken }) => {
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [username, setUsername] = useState("");
    const [usernameError, setUsernameError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false); // New state to track submission

    // New states for login by token and toggles
    const [tokenCode, setTokenCode] = useState("");
    const [tokenError, setTokenError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [loginMode, setLoginMode] = useState("password");

    const navigate = useNavigate();

    const onButtonClick = () => {
        // Reset error values
        setUsernameError("");
        setPasswordError("");

        // Validation
        if (username.trim() === "") {
            setUsernameError("Please enter a username");
            return;
        }

        if (password.trim() === "") {
            setPasswordError("Please enter a password");
            return;
        }

        if (password.length < 6) {
            setPasswordError("The password must be 6 characters or longer");
            return;
        }

        // Perform login
        logIn();
    };

    const onTokenLoginClick = () => {
        setTokenError("");
        if (tokenCode.trim() === "") {
            setTokenError("Please enter a token");
            return;
        }
        logInByToken();
    };

    // Username & password login
    const logIn = async () => {
        setIsSubmitting(true); // Disable button on submit
        try {
            const response = await fetch(`${API_BASE_URL}/auth/api/main/login`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, password })
            });

            const data = await response.json();

            if (data.token) {
                // Handle successful login
                localStorage.setItem("user", JSON.stringify({
                    username: data.username,
                    token: data.token,
                    refreshToken: data.expiresAt,
                    roles: data.roles,
                }));

                // Update state
                // setUser(username);
                setToken(data.token);

                const responseUser = await fetch(`${API_BASE_URL}/auth/api/main/user`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + data.token
                    },
                });

                const userData = await responseUser.json();

                if (userData.permissions) {
                    // Merge permissions into existing user object
                    const existingUser = JSON.parse(localStorage.getItem("user"));
                    const updatedUser = { ...existingUser, uid: userData.uid || null, permissions: userData.permissions };

                    localStorage.setItem("user", JSON.stringify(updatedUser));
                }

                setLoggedIn(true);

                // A stale dead-token backup from an earlier 403 in this tab must not
                // outlive a fresh successful login (see clearPermissionRecovery doc).
                clearPermissionRecovery();

                // Redirect to dashboard
                navigate("/");
            } else {
                setUsernameError("Wrong username or password");
                setPasswordError(""); // Clear any previous password error
            }
        } catch (error) {
            console.error("Login error:", error);
            setUsernameError("An error occurred. Please try again.");
            setPasswordError(""); // Clear any previous password error
        } finally {
            setIsSubmitting(false); // Re-enable button
        }
    };

    // Login by token (manual code)
    const logInByToken = async () => {
        setIsSubmitting(true);
        try {
            // Validate token by fetching user profile
            const responseUser = await fetch(`${API_BASE_URL}/auth/api/main/user`, {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + tokenCode
                },
            });

            if (!responseUser.ok) {
                throw new Error("Invalid token or authentication failed");
            }

            const userData = await responseUser.json();

            // Persist minimal user info with token
            const userToStore = {
                // uid is what every server-side identity check keys on. Without it the
                // UI had to fall back to comparing usernames to answer "is this row me?",
                // which is right today only because usernames happen to be unique.
                uid: userData.uid || null,
                username: userData.username || username || "",
                token: tokenCode,
                refreshToken: userData.expiresAt,
                roles: userData.roles,
                permissions: userData.permissions,
            };
            localStorage.setItem("user", JSON.stringify(userToStore));

            // Cache token in app state
            setToken(tokenCode);

            setLoggedIn(true);
            clearPermissionRecovery();
            navigate("/");
        } catch (error) {
            console.error("Token login error:", error);
            setTokenError("Invalid token or network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="mainContainer">
            <div className="logoContainer">
                <img src={`${process.env.PUBLIC_URL}/tk-bg3.png`} alt="Logo" className="logoImage" />
            </div>

            <div className="titleContainer">
                <h1>ลงชื่อเข้าใช้</h1>
                <div style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        className="toggleButton"
                        onClick={() => {
                            setLoginMode(prev => prev === "password" ? "token" : "password");
                            setUsernameError("");
                            setPasswordError("");
                            setTokenError("");
                        }}
                        aria-label="Switch login mode"
                    >
                        {loginMode === "password" ? "สลับเป็นโหมดโทเค็น" : "สลับเป็นโหมดรหัสผ่าน"}
                    </button>
                </div>
            </div>

            {loginMode === "password" && (
                <>
                    {/* Username */}
                    <div className="inputContainer">
                        <input
                            value={username}
                            placeholder="ป้อนชื่อผู้ใช้"
                            onChange={ev => setUsername(ev.target.value)}
                            className="inputBox"
                            aria-label="Username"
                        />
                        {usernameError && <label className="errorLabel">{usernameError}</label>}
                    </div>

                    {/* Password with show/hide toggle */}
                    <div className="inputContainer" style={{ position: "relative" }}>
                        <input
                            type={showPassword ? "text" : "password"}
                            value={password}
                            placeholder="ป้อนระหัสผ่าน"
                            onChange={ev => setPassword(ev.target.value)}
                            className="inputBox"
                            aria-label="Password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(prev => !prev)}
                            className="toggleButton"
                            style={{ position: "absolute", right: 8, top: 8 }}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? "ซ่อน" : "แสดง"}
                        </button>
                        {passwordError && <label className="errorLabel">{passwordError}</label>}
                    </div>

                    {/* Login by username/password */}
                    <div className="inputContainer">
                        <input
                            className="inputButton"
                            type="button"
                            onClick={onButtonClick}
                            value={isSubmitting ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
                            disabled={isSubmitting}
                        />
                    </div>
                </>
            )}

            {loginMode === "token" && (
                <>
                    {/* Token input with show/hide toggle */}
                    <div className="inputContainer" style={{ position: "relative" }}>
                        <input
                            type={showToken ? "text" : "password"}
                            value={tokenCode}
                            placeholder="ป้อนโทเค็น/รหัสผ่านแบบโค้ด"
                            onChange={ev => setTokenCode(ev.target.value)}
                            className="inputBox"
                            aria-label="Login token"
                        />
                        <button
                            type="button"
                            onClick={() => setShowToken(prev => !prev)}
                            className="toggleButton"
                            style={{ position: "absolute", right: 8, top: 8 }}
                            aria-label={showToken ? "Hide token" : "Show token"}
                        >
                            {showToken ? "ซ่อน" : "แสดง"}
                        </button>
                        {tokenError && <label className="errorLabel">{tokenError}</label>}
                    </div>

                    {/* Login by token button */}
                    <div className="inputContainer">
                        <input
                            className="inputButton"
                            type="button"
                            onClick={onTokenLoginClick}
                            value={isSubmitting ? "กำลังตรวจสอบโทเค็น..." : "เข้าสู่ระบบด้วยโทเค็น"}
                            disabled={isSubmitting}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Login;
